import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import {
  APP_BUILD_REF,
  APP_VERSION,
  APP_VERSION_DISPLAY,
  APP_VERSION_LABEL,
} from '../../app-version';
import { BUILD_BRANCH, BUILD_HISTORY, BUILD_SHA } from '../../build-info.generated';
import { APP_VERSION_NOTES_REGISTRY } from '../../version-notes';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';
import { Klok, klok } from '../../helpers/klok';
import {
  BuildHistoryDialogComponent,
  BuildHistoryDialogData,
} from '../build-history-dialog/build-history-dialog.component';
import {
  VersionNotesDialogComponent,
  VersionNotesDialogData,
} from '../version-notes-dialog/version-notes-dialog.component';

/** Item op de statusregel: vaste waarden of (nog) lege placeholders. */
export interface StatusBarItem {
  id: string;
  /** Korte labeltekst links van de waarde, bijv. "API". */
  label: string;
  /** Weergegeven waarde; "—" = nog niet aangesloten. */
  value: string;
  /** Tooltip met uitleg / toekomstige bron. */
  title: string;
  /** true = bewust nog leeg (placeholder). */
  placeholder?: boolean;
}

@Component({
  selector: 'app-status-bar',
  templateUrl: './status-bar.component.html',
  styleUrls: ['./status-bar.component.scss']
})
export class StatusBarComponent implements OnInit, OnDestroy {

  public readonly versionDisplay = APP_VERSION_DISPLAY;
  public readonly buildRef = APP_BUILD_REF;
  public readonly versionLabel = APP_VERSION_LABEL;
  public readonly envName = environment.envName;
  public readonly envTitle = this.envTooltip();

  public apiHost = '';
  public role = '';
  /** Huidige tijd in de tijdzone van het apparaat (waarin de console tijden toont). */
  public klok: Klok = klok(new Date());
  private klokTimer?: ReturnType<typeof setInterval>;

  /**
   * Placeholders rechts van de omgeving — klaar om later te vullen
   * (health-check, fleet-hub, recorder-telling, enz.).
   */
  public readonly placeholders: StatusBarItem[] = [
    {
      id: 'api',
      label: 'API',
      value: '—',
      title: 'API-status (online/latency) — later via health-check',
      placeholder: true
    },
    {
      id: 'fleet',
      label: 'Fleet',
      value: '—',
      title: 'Studio-pc’s online — later via fleet/dispatch',
      placeholder: true
    },
    {
      id: 'recorders',
      label: 'Recorders',
      value: '—',
      title: 'Actieve recorders — later via recorder-overzicht',
      placeholder: true
    },
    {
      id: 'sync',
      label: 'Sync',
      value: '—',
      title: 'Laatste catalogus-/tagging-sync — later via API-providers',
      placeholder: true
    }
  ];

  constructor(
    private authService: AuthService,
    private dialog: Dialog,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.apiHost = this.shortHost(environment.apiUrl);
    this.role = this.authService.user?.role ?? '';
    // Elke seconde, buiten Angular: alleen de statusregel wordt bijgewerkt,
    // niet de hele pagina.
    this.ngZone.runOutsideAngular(() => {
      this.klokTimer = setInterval(() => {
        this.klok = klok(new Date());
        this.cdr.detectChanges();
      }, 1000);
    });
  }

  ngOnDestroy(): void {
    if (this.klokTimer) {
      clearInterval(this.klokTimer);
    }
  }

  /** Klik op v1.0.3d → release-notes; links een versielijst (actueel + ouder). */
  public onOpenVersionNotes(): void {
    const data: VersionNotesDialogData = {
      currentVersion: APP_VERSION,
      registry: APP_VERSION_NOTES_REGISTRY,
      buildRef: APP_BUILD_REF,
    };
    this.dialog.open(VersionNotesDialogComponent, {
      data,
      panelClass: 'version-notes-dialog-panel',
    });
  }

  /** Klik op branch@sha → laatste merges + detail. */
  public onOpenBuildHistory(): void {
    const data: BuildHistoryDialogData = {
      branch: BUILD_BRANCH,
      sha: BUILD_SHA,
      versionLabel: APP_VERSION_LABEL,
      history: BUILD_HISTORY ?? [],
    };
    this.dialog.open(BuildHistoryDialogComponent, {
      data,
      panelClass: 'build-history-dialog-panel',
    });
  }

  private shortHost(url: string): string {
    try {
      return new URL(url).host.replace(/\.provispo\.tv$/i, '');
    } catch {
      return url;
    }
  }

  private envTooltip(): string {
    switch (environment.envName) {
      case 'prod':
        return 'Productie (playstats.ai / live API)';
      case 'china':
        return 'China-omgeving';
      default:
        return 'Development (dev.playstats.ai / siq-dev-*)';
    }
  }
}
